import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Menu,
  MenuItem,
  Skeleton,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import { CalendarDays, ChevronDown, Settings, History } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authstore';
import { useAcademicYear } from '../../hooks/useAcademicYear';
import { useAcademicYearStore } from '../../store/academicYearStore';
import { AcademicYear } from '../../types/academicYear';
import { ROUTES } from '../../router/routes';
import { trackEvent } from '../../lib/mixpanel';

const AcademicYearSelector: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { session } = useAuthStore();
  const instituteId = session?.user?.instituteId ?? 0;

  const { academicYears, isLoading } = useAcademicYear(instituteId);
  const {
    selectedAcademicYearId,
    selectedAcademicYearName,
    isViewingHistorical,
    setSelectedAcademicYear,
    setDefaultAcademicYear,
  } = useAcademicYearStore();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Sync store default when the academic years list changes (e.g., after setDefaultYear)
  useEffect(() => {
    if (academicYears.length > 0) {
      const defaultYear = academicYears.find((y) => y.isDefault);
      if (defaultYear) {
        // Pass name — store auto-switches selectedAcademicYearId if user was on the default
        setDefaultAcademicYear(defaultYear.id, defaultYear.name);
      }
    }
  }, [academicYears, setDefaultAcademicYear]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    trackEvent('Academic Year Selector Opened');
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectYear = (year: AcademicYear) => {
    setSelectedAcademicYear(year.id, year.name);
    trackEvent('Academic Year Changed', {
      year_id: year.id,
      year_name: year.name,
      is_default: year.isDefault,
    });
    handleClose();
  };

  const handleManageYears = () => {
    handleClose();
    navigate(ROUTES.SETTINGS.ACADEMIC_YEARS.LIST);
  };

  // Get the currently selected year object
  const currentYear = academicYears.find((y) => y.id === selectedAcademicYearId);
  const displayName = currentYear?.name || selectedAcademicYearName || 'Select Year';

  // Sort years: Active/Default first, then by date
  const sortedYears = [...academicYears]
    .filter((y) => y.status !== 'Archived')
    .sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
    });

  if (isLoading) {
    return <Skeleton variant="rounded" width={120} height={36} sx={{ mx: 1 }} />;
  }

  if (academicYears.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mx: 1 }}>
      <Button
        onClick={handleClick}
        variant="text"
        size="small"
        endIcon={<ChevronDown size={16} />}
        sx={{
          color: theme.palette.text.primary,
          bgcolor: isViewingHistorical
            ? alpha(theme.palette.warning.main, 0.1)
            : alpha(theme.palette.primary.main, 0.08),
          border: isViewingHistorical
            ? `1px solid ${alpha(theme.palette.warning.main, 0.3)}`
            : `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          borderRadius: 2,
          px: 1.5,
          py: 0.75,
          textTransform: 'none',
          fontWeight: 500,
          minWidth: 100,
          '&:hover': {
            bgcolor: isViewingHistorical
              ? alpha(theme.palette.warning.main, 0.15)
              : alpha(theme.palette.primary.main, 0.12),
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {isViewingHistorical ? (
            <History size={16} color={theme.palette.warning.main} />
          ) : (
            <CalendarDays size={16} color={theme.palette.primary.main} />
          )}
          <Typography
            variant="body2"
            sx={{
              maxWidth: 100,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displayName}
          </Typography>
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            minWidth: 220,
            mt: 1,
            borderRadius: 2,
            boxShadow: theme.shadows[8],
          },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            Academic Years
          </Typography>
        </Box>

        {sortedYears.map((year) => (
          <MenuItem
            key={year.id}
            onClick={() => handleSelectYear(year)}
            selected={year.id === selectedAcademicYearId}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 1,
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight={year.isDefault ? 600 : 400}>
                {year.name}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {year.isDefault && (
                <Chip
                  label="Active"
                  size="small"
                  color="success"
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              )}
              {year.status === 'Completed' && (
                <Chip
                  label="Past"
                  size="small"
                  color="default"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              )}
            </Box>
          </MenuItem>
        ))}

        <Divider sx={{ my: 1 }} />

        <MenuItem onClick={handleManageYears} sx={{ py: 1 }}>
          <Settings size={16} style={{ marginRight: 8 }} />
          <Typography variant="body2">Manage Years</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default AcademicYearSelector;
